import Dialog, { Content, Portal } from "@corvu/dialog";
import { MultiProvider } from "@solid-primitives/context";
import { MetaProvider, Title } from "@solidjs/meta";
import { Navigate, type RouteDefinition, Router, type RouteSectionProps } from "@solidjs/router";
import { type FirebaseOptions, initializeApp } from "firebase/app";
import { getAuth, signOut } from "firebase/auth";
import { FirebaseProvider } from "solid-firebase";
import { createResource, createSignal, ErrorBoundary, lazy, onMount, Show } from "solid-js";
import invariant from "tiny-invariant";
import * as uuid from "uuid";

import { Button } from "catcolab-ui-components";
import { Api, ApiContext, useApi } from "./api";
import { helpRoutes } from "./help/routes";
import { createModelLibraryWithApi, ModelLibraryContext } from "./model";
import { createModel } from "./model/document";
import { ErrorBoundaryDialog } from "./page/error_boundary";
import { PageContainer } from "./page/page_container";
import { stdTheories } from "./stdlib";
import { TheoryLibraryContext } from "./theory";
import { InferenceKeyProvider } from "./user/inference_key_provider";
import { UserStateProvider } from "./user/user_state_provider";

const serverUrl = import.meta.env.VITE_SERVER_URL;
const repoUrl = import.meta.env.VITE_AUTOMERGE_REPO_URL;
const firebaseOptions = JSON.parse(import.meta.env.VITE_FIREBASE_OPTIONS) as FirebaseOptions;

const Root = (props: RouteSectionProps) => {
    invariant(serverUrl, "Must set environment variable VITE_SERVER_URL");
    invariant(repoUrl, "Must set environment variable VITE_AUTOMERGE_REPO_URL");

    const firebaseApp = initializeApp(firebaseOptions);
    const api = new Api({ serverUrl, repoUrl, firebaseApp });

    const [isSessionInvalid, setIsSessionInvalid] = createSignal(false);

    onMount(async () => {
        const result = await api.rpc.validate_session.query();
        if (result.tag === "Err") {
            await signOut(getAuth(firebaseApp));
            setIsSessionInvalid(true);
        }
    });

    const theories = stdTheories;
    const models = createModelLibraryWithApi(api, theories);

    return (
        <FirebaseProvider app={firebaseApp}>
            <MultiProvider
                values={[
                    [ApiContext, api],
                    [TheoryLibraryContext, theories],
                    [ModelLibraryContext, models],
                    UserStateProvider,
                    InferenceKeyProvider,
                ]}
            >
                <MetaProvider>
                    <Title>{import.meta.env.VITE_APP_TITLE}</Title>
                    <ErrorBoundary fallback={(err) => <ErrorBoundaryDialog error={err} />}>
                        <PageContainer>{props.children}</PageContainer>
                    </ErrorBoundary>
                    <Show when={isSessionInvalid()}>
                        <SessionExpiredModal />
                    </Show>
                </MetaProvider>
            </MultiProvider>
        </FirebaseProvider>
    );
};

// Why this needs to be a separate modal: we cannot automatically reload the
// page because a bug in validate_session might trigger an infinite reload loop,
// so the reload must be user-triggered. Although ErrorBoundary might seem like
// the natural place to handle this, it only catches the first error, and
// there's no guarantee that an error from validate_session will be the first
// one encountered.
export function SessionExpiredModal() {
    const [reloading, setReloading] = createSignal(false);

    const handleReload = () => {
        setReloading(true);
        location.reload();
    };

    return (
        <Dialog initialOpen={true}>
            <Portal>
                <Content class="popup error-dialog">
                    <h3>Session Expired</h3>
                    <p>Your session is no longer valid. Please reload the page to continue.</p>
                    <Button variant="positive" onClick={handleReload} disabled={reloading()}>
                        {reloading() ? "Reloading..." : "Reload Page"}
                    </Button>
                </Content>
            </Portal>
        </Dialog>
    );
}

function CreateModel() {
    const api = useApi();

    const [ref] = createResource<string>(() => {
        return createModel(api, stdTheories.defaultTheoryMetadata().id);
    });

    return <Show when={ref()}>{(ref) => <Navigate href={`/model/${ref()}`} />}</Show>;
}

const HomePage = lazy(() => import("./page/home_page"));

const refIsUUIDFilter = {
    ref: (ref: string) => uuid.validate(ref),
};

const routes: RouteDefinition[] = [
    {
        path: "/",
        component: HomePage,
    },
    {
        path: "/new",
        component: CreateModel,
    },
    {
        path: "/:kind/:ref/:subkind?/:subref?",
        matchFilters: {
            kind: ["model", "diagram", "analysis"],
            ref: refIsUUIDFilter.ref,
            subkind: (v?: string) => !v || v === "analysis" || v === "diagram" || v === "model",
            subref: (v?: string) => !v || refIsUUIDFilter.ref(v),
        },
        component: lazy(() => import("./page/document_page")),
    },
    {
        path: "/help",
        component: lazy(() => import("./help/help_layout")),
        children: helpRoutes,
    },
    {
        path: "/dev/*",
        component: (route) => {
            const url = `https://next.catcolab.org${route.location.pathname}`;
            window.location.replace(url);
            return null;
        },
    },
    {
        path: "/rfc/*",
        component: (route) => {
            const url = `https://next.catcolab.org${route.location.pathname}`;
            window.location.replace(url);
            return null;
        },
    },
    {
        path: "/profile",
        component: lazy(() => import("./user/profile")),
    },
    {
        path: "/documents",
        component: lazy(() => import("./user/documents")),
    },
    {
        path: "/trash",
        component: lazy(() => import("./user/trash")),
    },
    {
        path: "*",
        component: lazy(() => import("./page/404_page")),
    },
];

function App() {
    // We need two "top-level" error boundaries in order to display the SessionExpiredModal even after an
    // error occurs, while also catching error created by the router or other providers
    return (
        <ErrorBoundary fallback={(err) => <ErrorBoundaryDialog error={err} />}>
            <Router root={Root}>{routes}</Router>
        </ErrorBoundary>
    );
}

export default App;
